import { useCallback, useEffect, useState } from "react";

/**
 * Tiny paginated-list hook for the admin tables.
 *
 * Why custom (no react-query)? The admin app is intentionally lightweight and
 * already uses bare axios everywhere. Adding a query lib for 4 admin tables
 * isn't worth the bundle bloat.
 *
 * Returns:
 *   { data, total, page, pageSize, loading, refreshing, error, setPage, setFilters, refetch }
 *
 * `fetcher` must accept `{ page, pageSize, ...filters }` and resolve to the API
 * payload (which must include `total` and the data array under `dataKey`).
 */
export function useAdminList({ fetcher, dataKey, defaultPageSize = 50, defaultFilters = {} }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [filters, setFiltersState] = useState(defaultFilters);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const payload = await fetcher({ page, pageSize, ...filters });
        setData(payload?.[dataKey] || []);
        setTotal(payload?.total || 0);
      } catch (err) {
        setError(err.response?.data?.error || err.response?.data?.message || err.message || "Failed to load");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetcher, dataKey, page, pageSize, filters],
  );

  useEffect(() => {
    load();
  }, [load]);

  const setFilters = useCallback((next) => {
    setPage(1);
    setFiltersState((prev) => ({ ...prev, ...next }));
  }, []);

  return {
    data,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    loading,
    refreshing,
    error,
    refetch: () => load({ silent: true }),
  };
}

export function fmtDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export function fmtRelative(value) {
  if (!value) return "—";
  const d = new Date(value).getTime();
  if (!Number.isFinite(d)) return "—";
  const diff = Date.now() - d;
  const abs = Math.abs(diff);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (abs < minute) return "just now";
  if (abs < hour) return `${Math.round(diff / minute)}m ago`;
  if (abs < day) return `${Math.round(diff / hour)}h ago`;
  return `${Math.round(diff / day)}d ago`;
}
